import { useState, useEffect, useCallback } from 'react';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  roles: string[];
  clientId?: string;
}

export interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Azure AD / Entra ID auth hook for SWA built-in authentication.
 * Works with Azure Static Web Apps /.auth/me endpoint.
 */
export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const fetchUser = useCallback(async () => {
    try {
      const res = await fetch('/.auth/me');
      if (!res.ok) {
        setState({ user: null, isAuthenticated: false, isLoading: false, error: null });
        return;
      }
      const data = await res.json();
      const principal = data.clientPrincipal;
      if (!principal) {
        setState({ user: null, isAuthenticated: false, isLoading: false, error: null });
        return;
      }
      // Extract display name from Azure AD claims (fallback chain)
      const claims = principal.claims ?? [];
      const displayName =
        claims.find((c: { typ: string; val: string }) => c.typ === 'name')?.val ??
        claims.find((c: { typ: string; val: string }) =>
          c.typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'
        )?.val ??
        claims.find((c: { typ: string; val: string }) => c.typ === 'preferred_username')?.val ??
        principal.userDetails;

      const user: AuthUser = {
        id: principal.userId,
        name: displayName,
        email: principal.userDetails,
        roles: principal.userRoles ?? [],
        clientId: claims.find((c: { typ: string }) => c.typ === 'client_id')?.val,
      };
      setState({ user, isAuthenticated: true, isLoading: false, error: null });
    } catch (err) {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Auth check failed',
      });
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback((provider: string = 'aad') => {
    window.location.href = `/.auth/login/${provider}`;
  }, []);

  const logout = useCallback(() => {
    window.location.href = '/.auth/logout';
  }, []);

  const hasRole = useCallback(
    (role: string) => state.user?.roles.includes(role) ?? false,
    [state.user]
  );

  const isAdmin = state.user?.roles.includes('admin') ?? false;
  const isClient = state.user?.roles.includes('client') ?? false;

  return { ...state, login, logout, hasRole, isAdmin, isClient, refresh: fetchUser };
}
